/**
 * Structural sharing for the plan document.
 *
 * `edit()` mutates a deep copy — simple, and it keeps every mutation from
 * touching the state an undo snapshot or a pending save still refers to. But a
 * deep copy gives every table and every guest a new identity, so after one
 * table moves all of them look changed to React: on a 120-table wedding that
 * was every table and 1,224 chairs re-rendered on every frame of a drag. This
 * hands the previous objects back wherever the edit left them equal, which is
 * what lets `TableNode` and `SeatNode` skip the work.
 */

import { eq } from './merge'
import type { Plan } from './types'

function shareList<T extends { id: string }>(previous: readonly T[], next: T[]): T[] {
  const before = new Map(previous.map((item) => [item.id, item]))
  let unchanged = previous.length === next.length
  const shared = next.map((item, index) => {
    const old = before.get(item.id)
    const kept = old !== undefined && eq(old, item) ? old : item
    if (kept !== previous[index]) unchanged = false
    return kept
  })
  return unchanged ? (previous as T[]) : shared
}

/** `next`, reusing `previous`'s tables, guests and settings wherever they are equal. */
export function shareUnchanged(previous: Plan, next: Plan): Plan {
  if (previous === next) return next
  return {
    ...next,
    tables: shareList(previous.tables, next.tables),
    guests: shareList(previous.guests, next.guests),
    settings: eq(previous.settings, next.settings) ? previous.settings : next.settings,
  }
}
